import { FormControlProps, OutlinedInput, OutlinedInputProps } from "@mui/material";
import React, { forwardRef, ReactElement } from "react";
import { FormControlWrapper, InputWrapperProps } from "../FormControlWrapper/FormControlWrapper";
import { useNumberInputValue } from "@sinch/components/form/hooks/useNumberInputValue";

export type TextInputProps<TType extends string = "text"> = OutlinedInputProps &
  Omit<InputWrapperProps, "slots" | "slotProps"> & {
    FormControlProps?: FormControlProps;
    type?: TType;
  } & (TType extends "number"
    ? {
        /**
         * Allow empty value for number input
         */
        allowEmpty?: boolean;
      }
    : { allowEmpty?: never });

/**
 * Outlined text input see [https://mui.com/material-ui/api/outlined-input/](https://mui.com/material-ui/api/outlined-input/)
 */
export const TextInputBase = <TType extends string = "text">({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  onChange,
  value,
  allowEmpty,
  ...props
}: TextInputProps<TType>): ReactElement => {
  const isNumberType = props.type === "number";

  const {
    toClosestLimit,
    toNumber,
    value: numberValue,
  } = useNumberInputValue({
    value,
    allowEmpty,
    onChange,
    min: props.inputProps?.min,
    max: props.inputProps?.max,
  });

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <OutlinedInput
        error={error}
        {...props}
        onBlur={(e) => {
          props?.onBlur?.(e);
          if (isNumberType) {
            e.target.value = toClosestLimit(e.target.value);
            onChange?.(e);
          }
        }}
        onChange={(e) => {
          if (isNumberType) {
            e.target.value = toNumber(e.target.value);
          }
          onChange?.(e);
        }}
        type={isNumberType ? "text" : props.type}
        value={isNumberType ? numberValue : value}
      />
    </FormControlWrapper>
  );
};

export const TextInput = forwardRef(TextInputBase) as <TType extends string = "text">(
  props: TextInputProps<TType>
) => ReturnType<typeof TextInputBase>;
